import { get, increment, limitToLast, push, query, ref, set, update } from "firebase/database";
import { database } from "./firebase";
import { checkExpiration } from "../util/util"

const MAIN_COURSE_SLUGS = [
    "analise-tatica-futebol",
    "leitura-jogo-apostas-esportivas",
    "edicao-video-producao-relatorios",
];

const hasCommentIdentity = (comment) => Boolean(comment?.userUid || String(comment?.comment || "").trim());

const collapseLegacyCommentChains = (comments) => {
    const source = comments || {};
    const placeholderIds = new Set(
        Object.entries(source)
            .filter(([, comment])=>!hasCommentIdentity(comment) && Object.keys(comment?.replies || {}).length > 0)
            .map(([commentId])=>commentId)
    );
    const consumedPlaceholders = new Set();
    const result = {};

    const appendReplies = (replies, flattened, visited = new Set()) => {
        Object.entries(replies || {}).forEach(([replyId, reply])=>{
            flattened[replyId] = reply;
            if(placeholderIds.has(replyId) && !visited.has(replyId)){
                visited.add(replyId);
                consumedPlaceholders.add(replyId);
                appendReplies(source[replyId]?.replies, flattened, visited);
            }
        });
    };

    Object.entries(source).forEach(([commentId, comment])=>{
        if(placeholderIds.has(commentId)){
            return;
        }
        const replies = {};
        appendReplies(comment?.replies, replies);
        result[commentId] = {
            ...comment,
            ...(Object.keys(replies).length > 0 ? { replies } : {}),
        };
    });

    // Evita esconder dados caso apareça um fragmento legado sem o comentário raiz.
    [...placeholderIds]
        .filter((commentId)=>!consumedPlaceholders.has(commentId))
        .forEach((commentId)=>{
            Object.entries(source[commentId]?.replies || {}).forEach(([replyId, reply])=>{
                const replies = {};
                if(placeholderIds.has(replyId)){
                    appendReplies(source[replyId]?.replies, replies, new Set([replyId]));
                }
                result[replyId] = {
                    ...reply,
                    ...(Object.keys(replies).length > 0 ? { replies } : {}),
                };
            });
        });

    return result;
};

async function writeDiscussionComment({ sourceType, sourceId, courseId = null, parentCommentId = null, comment, userUid }){
    const normalizedComment = String(comment || "").trim();
    if(!normalizedComment){
        return null;
    }

    const commentsPath = `/contents/contentsData/${sourceId}/comments`;
    const createdAt = Date.now();

    if(!parentCommentId){
        const commentRef = push(ref(database, commentsPath));
        await set(commentRef, {
            comment: normalizedComment,
            userUid,
            createdAt,
            sourceType,
            courseId: courseId || "",
        });
        return { key: commentRef.key };
    }

    const replyRef = push(ref(database, `${commentsPath}/${parentCommentId}/replies`));
    await set(replyRef, {
        comment: normalizedComment,
        userUid,
        createdAt,
        sourceType,
        courseId: courseId || "",
    });
    return { key: replyRef.key };
}



/*
USUÁRIOS
*/

export async function checkCourseAccess(userId, courseSlug){
    try{
        const adminSnapshot = await get(ref(database, `/admins/${userId}`)).catch(()=>null);
        if(adminSnapshot?.exists()){
            return true;
        }
        const path = courseSlug === 'formacao' ? `/users/${userId}/program/expiration` : `/users/${userId}/courses/${courseSlug}/expiration`;
        const dbRef = ref(database, path);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return checkExpiration(snapshot.val());
            }else{                
                return false;
            }
        }) 
    }catch (error){
        console.error("Erro requisitando acesso do usuário", error);
        return false;
    }
}

export async function getUserMainAccessType(userId){
    if(!userId){
        return "not_logged";
    }

    try{
        const adminSnapshot = await get(ref(database, `/admins/${userId}`)).catch(()=>null);
        if(adminSnapshot?.exists()){
            // Mantém o administrador dentro das rotas de cursos; o acesso individual
            // às aulas e à formação é liberado por checkCourseAccess.
            return "course_access";
        }
        const [programSnapshot, courseSnapshots] = await Promise.all([
            get(ref(database, `/users/${userId}/program/expiration`)),
            Promise.all(
                MAIN_COURSE_SLUGS.map((courseSlug) => get(ref(database, `/users/${userId}/courses/${courseSlug}/expiration`)))
            ),
        ]);

        const hasProgramAccess = programSnapshot.exists() && checkExpiration(programSnapshot.val());
        if(hasProgramAccess){
            return "program_access";
        }

        const hasAnyCourseAccess = courseSnapshots.some((snapshot) => snapshot.exists() && checkExpiration(snapshot.val()));
        if(hasAnyCourseAccess){
            return "course_access";
        }

        return "no_access";
    }catch (error){
        console.error("Erro ao chamar getUserMainAccessType().", error);
        return "no_access";
    }
}

export async function getUserPublicInfo(userUid){
    try{
        const dbRef = ref(database, `/users/${userUid}/public`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                //console.log("Usuário não encontrado:", userUid);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getUserPublicInfo().", error);
        return null;
    }
}

export async function getUserPrivateData(userUid){
    try{
        const dbRef = ref(database, `/users/${userUid}/private`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                //console.log("Usuário não encontrado:", userUid);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getUserPrivateData().", error);
        return null;
    }
}

export async function setUserData(userUid,data){
    try{
        const dbRef = ref(database, `/users/${userUid}`);        
        await update(dbRef,data);
        return true;
    }catch (error){
        console.error("Erro ao chamar setUserPersonalData().", error);
        return null;
    }
}

export async function getUserLessonProgress(userId, contentSlug){
    try{
        const dbRef = ref(database, `/users/${userId}/contents/${contentSlug}/progress`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{                
                return 0;
            }
        })
    }catch (error){
        console.error("Erro requisitando getUserLessonProgress", error);
        return false;
    }
}

export async function setUserLessonProgress(userId, contentSlug, progress){
    try{
        const dbRef = ref(database, `/users/${userId}/contents/${contentSlug}`);
        return await set(dbRef,{progress: progress});
    }catch (error){
        console.error("Erro requisitando getUserLessonProgress", error);
        return false;
    }
}

export async function getUserBonus(userId){
    try{
        const dbRef = ref(database, `/users/${userId}/bonus`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{                
                return null;
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getUserBonus().", error);
        return null;
    }
}

export async function getUserCourses(userId){
    try{
        const dbRef = ref(database, `/users/${userId}/courses`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return Object.keys(snapshot.val());
            }else{                
                return [];
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getUserCourses().", error);
        return null;
    }
}

/*
CURSOS
*/

export async function getCourseList(){
    try{
        const dbRef = ref(database, `/courses/coursesList`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return Object.keys(snapshot.val());
            }else{
                console.log("/courses/coursesList Lista de Cursos não encontrada");
                return null;
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getCourseList().", error);
        return null;
    }
}

export async function getCourseProductHero(courseSlug){
    try{
        const dbRef = ref(database, `/courses/coursesData/${courseSlug}/courseProductHero`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return {...snapshot.val(),courseId: courseSlug};
            }else{
                console.log("/courseProductHero do Curso não encontrado:", courseSlug);
                return null;
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getCourseProductHero(courseslug).", error);
        return null;
    }
}

export async function getCoursePage(courseSlug){
    try{
        const dbRef = ref(database, `/courses/coursesData/${courseSlug}/coursePage`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                console.log("/coursePage do Curso não encontrado:", courseSlug);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getCoursePage(courseSlug).", error);
        return null;
    }
}

export async function getCourseProgress(userId, courseId){
    try{
        const courseContentPath = courseId === 'formacao'
            ? '/program/content'
            : `/courses/coursesData/${courseId}/coursePage/courseContent`;
        const dbRefCourse = ref(database, courseContentPath);
        const dbRefUser = ref(database, `/users/${userId}/contents`);
        let courseContent;
        let userProgress;
        let totalProgress = 0;
        let totalItems = 0;

        await get(dbRefCourse).then((snapshot)=>{
            if(snapshot.exists()){                
                courseContent = snapshot.val();
            }
        })

        await get(dbRefUser).then((snapshot)=>{
            if(snapshot.exists()){                
                userProgress = snapshot.val();
            }else{
                userProgress = [];
            }
        })

        courseContent.forEach(modulo => {
            modulo.items.forEach(aula => {
                totalItems++;
                if (userProgress[aula] && userProgress[aula].progress === 1) {
                    totalProgress++;
                }
            });
        });
        
        return totalItems === 0 ? 0 : totalProgress / totalItems;

    }catch (error){
        console.error("Erro ao chamar getCourseProgress().", error);
        return null;
    }
}

export async function getCourseFirstIncompletedContent(userId, courseId){
    try{
        const courseContentPath = courseId === 'formacao'
            ? '/program/content'
            : `/courses/coursesData/${courseId}/coursePage/courseContent`;
        const dbRefCourse = ref(database, courseContentPath);
        const dbRefUser = ref(database, `/users/${userId}/contents`);
        let courseContent;
        let userProgress;

        await get(dbRefCourse).then((snapshot)=>{
            if(snapshot.exists()){                
                courseContent = snapshot.val();
            }
        })

        await get(dbRefUser).then((snapshot)=>{
            if(snapshot.exists()){                
                userProgress = snapshot.val();
            }else{
                userProgress = [];
            }
        })

        for (let i = 0; i < courseContent.length; i++) {
            const modulo = courseContent[i];
            for (let j = 0; j < modulo.items.length; j++) {
                const aula = modulo.items[j];
                if (!userProgress[aula] || userProgress[aula].progress !== 1) {
                    return aula;
                }
            }
        }

        return null;

    }catch (error){
        console.error("Erro ao chamar getCourseFirstIncompletedContent().", error);
        return null;
    }
}

/*
FORMAÇÃO
*/

export async function getProgramContent(){
    try{
        const dbRef = ref(database, `/program/content`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                console.log("Conteúdo da Formação não encontrado.");
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getProgramContent().", error);
        return null;
    }
}

export async function getProgramContentCard(contentSlug,contentIndex){
    try{
        const dbRefImageUrl = ref(database, `/contents/contentsData/${contentSlug}/accessPrompt/imageUrl`);
        const dbRefTitle = ref(database, `/contents/contentsData/${contentSlug}/accessPrompt/title`);
        let imageUrl;
        let title;
        let parent = `Aula ${contentIndex + 1}`;

        await get(dbRefImageUrl).then((snapshot)=>{
            if(snapshot.exists()){                
                imageUrl = snapshot.val();
            }
        })

        await get(dbRefTitle).then((snapshot)=>{
            if(snapshot.exists()){                
                title = snapshot.val();
            }
        })

        const newCard = {
            imageUrl: imageUrl, 
            parent: parent, 
            title:  title, 
            href: `/formacao/${contentSlug}`
        }

        return newCard;

    }catch (error){
        console.error("Erro ao chamar getProgramContentCard().", error);
        return null;
    }
}


/*
CONTEÚDOS
*/

export async function getLessonPage(lessonSlug){
    try{
        const dbRef = ref(database, `/contents/contentsData/${lessonSlug}`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                console.log("Aula não encontrada:", lessonSlug);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getLessonPage(lessonSlug).", error);
        return null;
    }
}

export async function getContentCard(courseSlug,contentSlug,contentIndex){
    try{
        const dbRefImageUrl = ref(database, `/contents/contentsData/${contentSlug}/accessPrompt/imageUrl`);
        const dbRefTitle = ref(database, `/contents/contentsData/${contentSlug}/accessPrompt/title`);
        let imageUrl;
        let title;
        let parent = `Aula ${contentIndex + 1}`;

        await get(dbRefImageUrl).then((snapshot)=>{
            if(snapshot.exists()){                
                imageUrl = snapshot.val();
            }
        })

        await get(dbRefTitle).then((snapshot)=>{
            if(snapshot.exists()){                
                title = snapshot.val();
            }
        })

        const newCard = {
            imageUrl: imageUrl, 
            parent: parent, 
            title:  title, 
            href: `/cursos/${courseSlug}/${contentSlug}`
        }

        return newCard;

    }catch (error){
        console.error("Erro ao chamar getContentCard().", error);
        return null;
    }
}

export async function getContentComments(contentSlug){
    try{
        const dbRef = ref(database, `/contents/contentsData/${contentSlug}/comments`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return collapseLegacyCommentChains(snapshot.val());
            }else{
                //console.log("Aula não encontrada:", contentSlug);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getContentComments().", error);
        return null;
    }
}

export async function addContentComments(courseId,contentSlug,parentUid,comment,userUid){
    try{
        const title = await getContentTitle(contentSlug);
        return await writeDiscussionComment({
            sourceType: "lesson",
            sourceId: contentSlug,
            courseId,
            title,
            parentCommentId: parentUid,
            comment,
            userUid,
        });
    }catch (error){
        console.error("Erro ao chamar addContentComments().", error);
        return null;
    }
}

export async function getContentLinks(courseSlug,contentSlug){
    
    try{
        const path = courseSlug === 'formacao'
            ? '/program/content'
            : `/courses/coursesData/${courseSlug}/coursePage/courseContent`;

        const dbRefModules = ref(database, path);
        return await get(dbRefModules).then((snapshot)=>{
            if(snapshot.exists()){                

                const modules = snapshot.val();
                let moduleIndex = null;
                let contentIndex = null;
                
                for (let moduleIndexTemp = 0; moduleIndexTemp < modules.length; moduleIndexTemp++) {
                    const modulo = modules[moduleIndexTemp];
                    const contents = modulo.items;
                    for (let contentIndexTemp = 0; contentIndexTemp < contents.length; contentIndexTemp++) {
                        if (contents[contentIndexTemp] === contentSlug) {                            
                            moduleIndex = moduleIndexTemp;
                            contentIndex = contentIndexTemp;
                        }
                    }
                }
                
                const modulo = modules[moduleIndex];
                const items = modulo.items;
                let prevLink = null;
                let nextLink = null;
                let courseLink = courseSlug === 'formacao' ? `/formacao` : `/cursos/${courseSlug}`;

                if (contentIndex >= 0 && contentIndex < items.length) {                    

                    if (contentIndex === 0) {                        
                        if (moduleIndex > 0) {
                            const moduloAnterior = modules[moduleIndex - 1];
                            const aulasModuloAnterior = moduloAnterior.items;                            
                            prevLink = courseSlug === 'formacao' ?
                            `/formacao/${aulasModuloAnterior[aulasModuloAnterior.length - 1]}` :
                            `/cursos/${courseSlug}/${aulasModuloAnterior[aulasModuloAnterior.length - 1]}`;
                        }
                    } else {                        
                        prevLink = `/cursos/${courseSlug}/${items[contentIndex - 1]}`;
                    }
                    
                    if (contentIndex === items.length - 1) {                        
                        if (moduleIndex < modules.length - 1) {                            
                            nextLink = courseSlug === 'formacao' ?
                            `/formacao/${modules[moduleIndex + 1].items[0]}` :
                            `/cursos/${courseSlug}/${modules[moduleIndex + 1].items[0]}`;
                        }
                    } else {                        
                        nextLink = courseSlug === 'formacao' ?
                            `/formacao/${items[contentIndex + 1]}` :
                            `/cursos/${courseSlug}/${items[contentIndex + 1]}`;
                    }

                    return { prevLink, courseLink, nextLink };

                } else {
                    console.log("Índice de aula inválido");
                    return null;
                }
            }
        })
    }catch (error){
        console.error("Erro ao executar getContentLinks().",error);
        return null;
    }
}

export async function getContentTitle(contentSlug){
    try{
        const dbRef = ref(database, `/contents/contentsData/${contentSlug}/accessPrompt/title`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                //console.log("Aula não encontrada:", contentSlug);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getContentTitle().", error);
        return null;
    }
}

/*
CASES
*/

export async function getCasesList(){
    try{
        const dbRef = ref(database, `/contents/contentsList`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return Object.keys(snapshot.val());
            }else{
                return [];
            }
        })
    }catch (error){
        console.error("Erro ao chamar getCasesList().", error);
        return [];
    }
}

export async function getCase(caseId){
    try{
        const dbRef = ref(database, `/contents/contentsData/${caseId}`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists() && snapshot.val()?.type === "case"){
                return { caseId, ...snapshot.val() };
            }else{
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getCase().", error);
        return null;
    }
}

export async function getPublishedCases(){
    try{
        const casesList = await getCasesList();
        const casesData = await Promise.all(casesList.map((caseId)=>getCase(caseId)));
        return casesData
            .filter((caseData)=>caseData && caseData.isPublished)
            .sort((a,b)=>(Number(a.createdAt) || 0) - (Number(b.createdAt) || 0));
    }catch (error){
        console.error("Erro ao chamar getPublishedCases().", error);
        return [];
    }
}

export async function getUserCaseProgress(userId, caseId){
    try{
        const dbRef = ref(database, `/users/${userId}/cases/${caseId}`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getUserCaseProgress().", error);
        return null;
    }
}

export async function startUserCase(userId, caseId){
    try{
        const existingProgress = await getUserCaseProgress(userId, caseId);
        if(existingProgress?.startedAt){
            return existingProgress;
        }

        const startedAt = Date.now();
        const newProgress = {
            startedAt,
            status: "in_progress",
            currentPhaseIndex: 0,
        };

        const dbRef = ref(database, `/users/${userId}/cases/${caseId}`);
        await update(dbRef, newProgress);
        return newProgress;
    }catch (error){
        console.error("Erro ao chamar startUserCase().", error);
        return null;
    }
}

export async function getUserCaseSubmission(userId, caseId){
    try{
        const dbRef = ref(database, `/users/${userId}/caseSubmissions/${caseId}`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getUserCaseSubmission().", error);
        return null;
    }
}

export async function saveUserCasePhaseAnswers(userId, caseId, phaseIndex, answers){
    try{
        const timestamp = Date.now();
        const updates = {};
        updates[`/users/${userId}/caseSubmissions/${caseId}/phaseAnswers/${phaseIndex}/answers`] = answers;
        updates[`/users/${userId}/caseSubmissions/${caseId}/phaseAnswers/${phaseIndex}/status`] = "saved";
        updates[`/users/${userId}/caseSubmissions/${caseId}/phaseAnswers/${phaseIndex}/updatedAt`] = timestamp;
        updates[`/users/${userId}/caseSubmissions/${caseId}/updatedAt`] = timestamp;
        updates[`/users/${userId}/caseSubmissions/${caseId}/phaseAnswers/${phaseIndex}/savedAt`] = timestamp;

        await update(ref(database), updates);
        return true;
    }catch (error){
        console.error("Erro ao chamar saveUserCasePhaseAnswers().", error);
        return false;
    }
}

export async function setUserCaseCompleted(userId, caseId){
    try{
        const updates = {};
        updates[`/users/${userId}/cases/${caseId}/status`] = "completed";
        updates[`/users/${userId}/cases/${caseId}/completedAt`] = Date.now();
        await update(ref(database), updates);
        return true;
    }catch (error){
        console.error("Erro ao chamar setUserCaseCompleted().", error);
        return false;
    }
}

export async function getCaseComments(caseId){
    try{
        const dbRef = ref(database, `/contents/contentsData/${caseId}/comments`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return collapseLegacyCommentChains(snapshot.val());
            }else{
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getCaseComments().", error);
        return null;
    }
}

export async function addCaseComment(caseId, parentUid, comment, userUid){
    try{
        const caseSnapshot = await get(ref(database, `/contents/contentsData/${caseId}/title`));
        return await writeDiscussionComment({
            sourceType: "case",
            sourceId: caseId,
            courseId: "formacao",
            title: caseSnapshot.val() || caseId,
            parentCommentId: parentUid,
            comment,
            userUid,
        });
    }catch (error){
        console.error("Erro ao chamar addCaseComment().", error);
        return null;
    }
}


/*
PRODUTOS
*/

export async function getProducts(){
    try{
        const dbRef = ref(database, `/products/`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return Object.values(snapshot.val());
            }else{                
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getProducts().", error);
        return null;
    }
}

export async function getProduct(productId){
    try{
        const dbRef = ref(database, `/products/${productId}`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{
                //console.log("Aula não encontrada:", contentSlug);
                return null;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getProduct().", error);
        return null;
    }
}

export async function getBonusHero(bonusId){
    try{
        const dbRef = ref(database, `/bonus/bonusData/${bonusId}`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return {...snapshot.val(),bonusId: bonusId};
            }else{
                console.log("Bônus não encontrado:", bonusId);
                return null;
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getBonusHero().", error);
        return null;
    }
}



/*
COMPRAS
*/


export async function getOrders(userUid){
    try{
        const dbRef = ref(database, `/users/${userUid}/orders`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{                
                return null;
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getOrders().", error);
        return null;
    }
}



/*
NOTIFICAÇÕES PARA USUÁRIOS
*/


export async function addUserNotification(userUid,title,message,link){
    try{

        const dbRefCount = ref(database);
        const updates = {};
        updates[`/users/${userUid}/notifications/notificationsCount`] = increment(1);
        update(dbRefCount, updates);

        const dbRef = ref(database, `/users/${userUid}/notifications/notificationsList`);
        const newNotification = {
            title: title,
            message: message,
            link: link
        }
        return await push(dbRef,newNotification);
    }catch (error){
        console.error("Erro ao chamar addUserNotification().", error);
        return null;
    }
}

export async function clearUserNotification(userUid){
    try{

        const dbRef = ref(database, `/users/${userUid}/notifications`);
        const notificationsCount = {
            notificationsCount: 0
        }
        return await update(dbRef,notificationsCount);
    }catch (error){
        console.error("Erro ao chamar clearUserNotification().", error);
        return null;
    }
}

export async function getUserNotifications(userUid){
    try{
        const dbRef = ref(database, `/users/${userUid}/notifications/notificationsList`);
        
        const dataQuery = query(dbRef, limitToLast(10));
        return await get(dataQuery).then((snapshot)=>{
            if(snapshot.val()){
                const data = snapshot.val();
                const dataArray = Object.entries(data).map(([key, value]) => ({ key, ...value }));
                dataArray.sort((a, b) => b.key.localeCompare(a.key));
                return dataArray;
            }else{                
                return null;
            }
        })        
    }catch (error){
        console.error("Erro ao chamar getUserNotifications().", error);
        return null;
    }
}

export async function getUserNewNotifications(userUid){
    try{
        const dbRef = ref(database, `/users/${userUid}/notifications/notificationsCount`);
        return await get(dbRef).then((snapshot)=>{
            if(snapshot.exists()){
                return snapshot.val();
            }else{                
                return 0;
            }
        })
    }catch (error){
        console.error("Erro ao chamar getUserNewNotifications().", error);
        return null;
    }
}
